import { useMemo, useEffect, useState } from "react"
import { useField } from "formik"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { CreateOrderDialogMode } from "../../../createOrderTypes"
import { SelectPriceListsDialog } from "./SelectPriceListsDialog"
import { SelectedPriceListItem } from "../../../createOrderTypes"
import { EmptyPriceListsDialog } from "./EmptyPriceListsDialog"
import {
    fetchMeetingServicePriceListsForUser,
    MeetingServicePriceListsQuery,
    MeetingServicePriceListsResponse,
} from "./priceListsApi"

type SelectPriceListsProps = Readonly<{
    userId: number
    onClose: () => void
    onSelect: (item: SelectedPriceListItem) => void
    handleCloseMainDialog: () => void
    mode: CreateOrderDialogMode
}>

const transformData = (data: MeetingServicePriceListsResponse) => {
    return data.merchants
}

export function SelectPriceLists({ userId, onClose, onSelect, handleCloseMainDialog, mode }: SelectPriceListsProps) {
    const [{ value: initPriceList }] = useField<SelectedPriceListItem | null>("initPriceList")
    const [autoSelected, setAutoSelected] = useState(false)

    const { data: merchants = [], isLoading } = useApiQuery(fetchMeetingServicePriceListsForUser, {
        queryName: MeetingServicePriceListsQuery.USER_MEETING_SERVICE_PRICE_LISTS,
        dependencies: { userId },
        select: transformData,
        enabled: !!userId,
    })

    const filteredMerchants = useMemo(() => {
        if (mode === "create" || !initPriceList) return merchants
        const { merchantId, shopId } = initPriceList
        return merchants.filter((m) => m.id === merchantId && m.shop?.id === shopId)
    }, [merchants, mode, initPriceList?.merchantId, initPriceList?.shopId, initPriceList])

    useEffect(() => {
        if (isLoading) return
        if (autoSelected) return
        if (!filteredMerchants.length) return
        if (filteredMerchants.length !== 1) return
        if (filteredMerchants[0].priceLists.length !== 1) return

        const merchant = filteredMerchants[0]
        const onlyPriceList = merchant.priceLists[0]

        onSelect({
            merchantId: merchant.id,
            merchantName: merchant.displayName,
            priceListId: onlyPriceList.id,
            priceListName: onlyPriceList.displayName,
            shopId: merchant.shop.id,
            isOnlyOnePriceList: true,
        })

        setAutoSelected(true)
    }, [isLoading, filteredMerchants, onSelect, onClose, autoSelected])

    if (isLoading) return null

    if (!filteredMerchants.length) {
        return <EmptyPriceListsDialog onClose={handleCloseMainDialog} />
    }

    if (filteredMerchants.length === 1 && filteredMerchants[0].priceLists.length === 1) {
        return null
    }

    return (
        <SelectPriceListsDialog onClose={onClose} merchants={filteredMerchants} onSelect={onSelect} userId={userId} />
    )
}
